import { createContext, useContext, useReducer, useEffect, useCallback } from 'react';
import { storageService } from '../utils/storageService';

const EXHIBITION_ACTIONS = {
  SET_EXHIBITIONS: 'SET_EXHIBITIONS',
  SET_SELECTED: 'SET_SELECTED',
  SET_LOADING: 'SET_LOADING',
  SET_ERROR: 'SET_ERROR',
  CLEAR_ERROR: 'CLEAR_ERROR'
};

const exhibitionReducer = (state, action) => {
  switch (action.type) {
    case EXHIBITION_ACTIONS.SET_EXHIBITIONS:
      return {
        ...state,
        exhibitions: action.payload,
        loading: false,
        error: null
      };
    case EXHIBITION_ACTIONS.SET_SELECTED:
      return {
        ...state,
        selectedExhibition: action.payload
      };
    case EXHIBITION_ACTIONS.SET_LOADING:
      return {
        ...state,
        loading: action.payload
      };
    case EXHIBITION_ACTIONS.SET_ERROR:
      return {
        ...state,
        error: action.payload,
        loading: false
      };
    case EXHIBITION_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };
    default:
      return state;
  }
};

// Updated initial state to use storage service
const initialState = {
  exhibitions: [],
  selectedExhibition: storageService.getSelectedExhibition(),
  loading: false,
  error: null
};

const ExhibitionContext = createContext();

export const ExhibitionProvider = ({ children }) => {
  const [state, dispatch] = useReducer(exhibitionReducer, initialState);

  // Actions - Use useCallback to prevent recreation on every render
  const setExhibitions = useCallback((exhibitions) => {
    dispatch({ type: EXHIBITION_ACTIONS.SET_EXHIBITIONS, payload: exhibitions });
  }, []);

  const setSelectedExhibition = useCallback((exhibition) => {
    dispatch({ type: EXHIBITION_ACTIONS.SET_SELECTED, payload: exhibition });
    
    // Update storage using storage service
    if (exhibition) {
      storageService.setSelectedExhibition(exhibition);
      console.log('📦 Selected exhibition saved to storage:', exhibition.label);
    } else {
      storageService.removeSelectedExhibition();
      console.log('🗑️ Selected exhibition removed from storage');
    }
  }, []);

  const setLoading = useCallback((loading) => {
    dispatch({ type: EXHIBITION_ACTIONS.SET_LOADING, payload: loading });
  }, []);

  const setError = useCallback((error) => {
    dispatch({ type: EXHIBITION_ACTIONS.SET_ERROR, payload: error });
  }, []);

  const clearError = useCallback(() => {
    dispatch({ type: EXHIBITION_ACTIONS.CLEAR_ERROR });
  }, []);

  // Listen for storage events from other tabs
  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key === 'selectedExhibition') {
        const newSelectedExhibition = storageService.getSelectedExhibition();
        if (newSelectedExhibition !== state.selectedExhibition) {
          dispatch({ type: EXHIBITION_ACTIONS.SET_SELECTED, payload: newSelectedExhibition });
          console.log('📦 Selected exhibition updated from storage event');
        }
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [state.selectedExhibition]);

  // Initialize selected exhibition from storage if not already set
  useEffect(() => {
    if (!state.selectedExhibition) {
      const storedExhibition = storageService.getSelectedExhibition();
      if (storedExhibition) {
        dispatch({ type: EXHIBITION_ACTIONS.SET_SELECTED, payload: storedExhibition });
        console.log('📦 Restored selected exhibition from storage:', storedExhibition.label);
      }
    }
  }, []); 

  const value = {
    ...state,
    setExhibitions,
    setSelectedExhibition,
    setLoading,
    setError,
    clearError
  };

  return (
    <ExhibitionContext.Provider value={value}>
      {children}
    </ExhibitionContext.Provider>
  );
};

export const useExhibition = () => {
  const context = useContext(ExhibitionContext);
  if (!context) {
    throw new Error('useExhibition must be used within ExhibitionProvider');
  }
  return context;
};

export default ExhibitionContext;